import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Star, Heart } from "lucide-react";
import { inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { CAT_LABEL } from "@/lib/categories";

export default function ProductCard({ product, index = 0 }) {
  const { addItem } = useCart();
  const { isSaved, toggle } = useWishlist();
  const saved = isSaved(product.id);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: (index % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="group relative flex flex-col border border-black/10 bg-white"
      data-testid={`product-card-${product.slug}`}
    >
      <button
        onClick={(e) => {
          e.preventDefault();
          toggle(product);
        }}
        className={`absolute right-3 top-3 z-20 flex h-9 w-9 items-center justify-center rounded-full border transition-colors ${
          saved ? "border-[#2F80ED] bg-[#2F80ED] text-white" : "border-black/10 bg-white/90 text-black/60 hover:text-[#2F80ED]"
        }`}
        data-testid={`wishlist-toggle-${product.slug}`}
        aria-label="Toggle wishlist"
      >
        <Heart className="h-4 w-4" fill={saved ? "currentColor" : "none"} />
      </button>

      <Link to={`/product/${product.slug}`} className="relative block overflow-hidden bg-white">
        <div className="absolute left-0 top-0 z-10 flex items-center gap-1 bg-black px-3 py-1.5 font-mono text-[10px] uppercase tracking-overline text-white">
          {CAT_LABEL[product.category]}
        </div>
        {product.stock < 20 && (
          <div className="absolute bottom-0 left-0 z-10 bg-[#34D8A0] px-3 py-1.5 font-mono text-[10px] uppercase tracking-overline text-black">
            Low Stock
          </div>
        )}
        <motion.img
          src={product.image}
          alt={product.name}
          className="aspect-[4/5] w-full object-contain p-4"
          whileHover={{ scale: 1.04 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        />
      </Link>

      <div className="flex flex-1 flex-col p-5">
        {product.brand && (
          <div className="mb-1 font-mono text-[10px] font-semibold uppercase tracking-overline text-[#2F80ED]" data-testid={`brand-${product.slug}`}>
            {product.brand}
          </div>
        )}
        <div className="mb-2 flex items-center gap-1 font-mono text-xs text-black/50">
          <Star className="h-3 w-3 fill-[#2F80ED] text-[#2F80ED]" />
          {product.rating} · {product.reviews_count} reviews
        </div>
        <Link to={`/product/${product.slug}`}>
          <h3 className="font-display text-lg font-semibold leading-tight tracking-tight transition-colors group-hover:text-[#2F80ED]">
            {product.name}
          </h3>
        </Link>
        <p className="mt-2 line-clamp-2 flex-1 text-sm text-black/55">{product.short_desc}</p>

        <div className="mt-5 flex items-end justify-between">
          <div className="flex items-baseline gap-2">
            <span className="font-mono text-lg font-bold">MRP {inr(product.price)}</span>
          </div>
          <button
            onClick={() => addItem(product)}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-black text-white transition-all hover:bg-[#2F80ED]"
            data-testid={`add-to-cart-${product.slug}`}
            aria-label="Add to cart"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}
